import { Arrange, Trash } from '@griply/assets';
import {
  CalendarDetailsVisibility,
  CalendarRef,
  ExternalConnection,
  ID,
  LinkedItemType,
  SyncMode,
} from '@griply/types';
import {
  getConnectionCalendar,
  getWriteBlockedMessageKey,
} from '@griply/utils';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarSelect } from 'shared/components/ui/calendar-select';
import {
  CustomFormSelect,
  Size as SelectSize,
} from 'shared/components/ui/custom-form-select';
import { FormFieldIcon } from 'shared/components/ui/form-field-icon';
import {
  Colors as IconButtonColors,
  IconButton,
  Sizes as IconButtonSizes,
} from 'shared/components/ui/icon-button';
import { ConnectionCalendar } from 'shared/hooks/queries/use-external-connections-calendars-queries';
import { Goal } from 'shared/types/goal';
import { LifeArea } from 'shared/types/life-area';

import { getCalendarSyncFieldOptions } from './calendar-sync-field-options';
import { CalendarSyncLinkedItemSelect } from './calendar-sync-linked-item-select';
import * as Styled from './calendar-sync-options.style';

export type CalendarSyncRowValue = {
  linkedItemId: ID | null;
  calendar: CalendarRef | null;
  mode: SyncMode | null;
  detailsVisibility: CalendarDetailsVisibility | null;
};

export type CalendarSyncOptionRowProps = CalendarSyncRowValue & {
  goals: Goal[];
  lifeAreas: LifeArea[];
  connections: ExternalConnection[];
  calendars: ConnectionCalendar[];
  selectableCalendars: ConnectionCalendar[];
  isLoadingCalendars: boolean;
  onChangeLinkedItem: (value: { id: ID; type: LinkedItemType } | null) => void;
  onChangeCalendar: (value: CalendarRef) => void;
  onChangeMode: (mode: SyncMode) => void;
  onChangeVisibility: (visibility: CalendarDetailsVisibility) => void;
  onRemove: () => void;
};

export const CalendarSyncOptionRow: React.FC<CalendarSyncOptionRowProps> = ({
  linkedItemId,
  calendar,
  mode,
  detailsVisibility,
  goals,
  lifeAreas,
  connections,
  calendars,
  selectableCalendars,
  isLoadingCalendars,
  onChangeLinkedItem,
  onChangeCalendar,
  onChangeMode,
  onChangeVisibility,
  onRemove,
}) => {
  const { t } = useTranslation();

  const { modeOptions, visibilityOptions } = getCalendarSyncFieldOptions(t);

  // The backend refuses every write to this row's chosen calendar — without the warning the row
  // looks healthy while the sync silently stopped. The calendar also drops out of the dropdown's
  // selectable set (getWritableConnectionCalendars), so this line is where the user learns why.
  const writeBlocked = getConnectionCalendar(calendars, calendar)?.writeBlocked;

  const onSelectMode = (value?: string) => {
    if (!value) {
      return;
    }

    onChangeMode(value as SyncMode);
  };

  const onSelectVisibility = (value?: string) => {
    if (!value) {
      return;
    }

    onChangeVisibility(value as CalendarDetailsVisibility);
  };

  const placeholder = t('calendar.sync-tasks.placeholder');

  return (
    <>
      <Styled.Row>
        <CalendarSyncLinkedItemSelect
          linkedItemId={linkedItemId}
          goals={goals}
          lifeAreas={lifeAreas}
          disabled={isLoadingCalendars}
          onChange={onChangeLinkedItem}
        />

        <Styled.SwapIcon>
          <FormFieldIcon icon={Arrange} />
        </Styled.SwapIcon>

        <CalendarSelect
          connections={connections}
          calendars={calendars}
          selectableCalendars={selectableCalendars}
          value={calendar}
          isLoading={isLoadingCalendars}
          onChange={onChangeCalendar}
        />

        <CustomFormSelect
          placeholder={placeholder}
          options={modeOptions}
          value={mode ?? undefined}
          onChange={onSelectMode}
          size={SelectSize.ExtraSmall}
          noOverflow
          disabled={isLoadingCalendars}
        />

        <CustomFormSelect
          placeholder={placeholder}
          options={visibilityOptions}
          value={detailsVisibility ?? undefined}
          onChange={onSelectVisibility}
          size={SelectSize.ExtraSmall}
          noOverflow
          disabled={isLoadingCalendars}
        />

        <IconButton
          type="button"
          size={IconButtonSizes.Small}
          color={IconButtonColors.Default}
          icon={Trash}
          onClick={onRemove}
          aria-label={t('calendar.sync-tasks.remove')}
        />
      </Styled.Row>

      {writeBlocked && (
        <Styled.RowWarning>
          {t(getWriteBlockedMessageKey(writeBlocked))}
        </Styled.RowWarning>
      )}
    </>
  );
};
